<div class="join-container">
  <div class="join-card-wrapper">

    <!-- ── Loading ─────────────────────────────────────────────── -->
    <div *ngIf="state === 'loading'" class="state-panel">
      <mat-spinner diameter="52"></mat-spinner>
      <p class="state-message">Verifying your invitation&hellip;</p>
    </div>

    <!-- ── Invalid / expired ────────────────────────────────────── -->
    <div *ngIf="state === 'invalid'" class="state-panel">
      <mat-icon class="state-icon state-icon--error">link_off</mat-icon>
      <h2>Invitation link expired</h2>
      <p class="state-message">{{ invalidMessage }}</p>
      <button mat-raised-button color="primary" (click)="goToLogin()">Go to Login</button>
    </div>

    <!-- ── Success ──────────────────────────────────────────────── -->
    <div *ngIf="state === 'success'" class="state-panel">
      <mat-icon class="state-icon state-icon--success">check_circle</mat-icon>
      <h2>Your facility is ready</h2>
      <p class="state-message">
        Your account and facility profile have been saved.<br>
        Sign in to manage your facility and invite staff users.
      </p>

      <div class="success-summary">
        <div class="summary-item">
          <mat-icon class="summary-icon">person</mat-icon>
          <span>Owner account created</span>
        </div>
        <div class="summary-item">
          <mat-icon class="summary-icon">local_hospital</mat-icon>
          <span>Facility profile completed</span>
        </div>
        <div class="summary-item">
          <mat-icon class="summary-icon">shield</mat-icon>
          <span>Security rules accepted</span>
        </div>
        <div class="summary-item">
          <mat-icon class="summary-icon">smartphone</mat-icon>
          <span>Phone 2FA required at first sign-in</span>
        </div>
      </div>

      <div class="info-box">
        <mat-icon class="info-box__icon">info_outline</mat-icon>
        <span>Only users with approved email domains can be invited to your facility.</span>
      </div>

      <button mat-raised-button color="primary" class="cta-btn" (click)="goToLogin()">
        <mat-icon>login</mat-icon>
        Sign in to invite staff
      </button>
    </div>

    <!-- ── Stepper card ─────────────────────────────────────────── -->
    <mat-card *ngIf="state === 'stepper' || state === 'submitting'" class="join-card">

      <div class="join-header">
        <div class="logo-row">
          <mat-icon class="logo-icon">medical_services</mat-icon>
          <span class="logo-text">NuageDX VNA</span>
        </div>
        <p class="join-subtitle">Complete your account and facility setup to join the NuageDX network.</p>
      </div>

      <!-- Invitation context banner -->
      <div *ngIf="invitation" class="invite-context">
        <div class="invite-context__item">
          <span class="invite-context__label">Invited facility</span>
          <span class="invite-context__value">{{ invitation.facilityName }}</span>
        </div>
        <div class="invite-context__item" *ngIf="invitation.hubName">
          <span class="invite-context__label">Invited by</span>
          <span class="invite-context__value">{{ invitation.hubName }}</span>
        </div>
        <div class="invite-context__item">
          <span class="invite-context__label">Account type</span>
          <span class="invite-context__value">Facility Owner</span>
        </div>
      </div>

      <mat-stepper #stepper [linear]="true" class="join-stepper">

        <!-- ── Step 1: Account ──────────────────────────────────── -->
        <mat-step [stepControl]="accountForm" label="Account">
          <ng-template matStepLabel>Account</ng-template>

          <div class="step-content">
            <h3 class="step-title">Create your owner account</h3>
            <p class="step-subtitle">This account will manage your facility profile and staff access.</p>

            <form [formGroup]="accountForm" novalidate autocomplete="off">

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Full name</mat-label>
                <mat-icon matPrefix>person</mat-icon>
                <input matInput formControlName="name" placeholder="Jane Smith" autocomplete="name">
                <mat-error *ngIf="accountForm.get('name')?.hasError('required')">Full name is required</mat-error>
                <mat-error *ngIf="accountForm.get('name')?.hasError('minlength')">At least 2 characters required</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width email-field">
                <mat-label>Email address</mat-label>
                <mat-icon matPrefix>email</mat-icon>
                <input matInput [value]="invitation?.email || ''" readonly tabindex="-1">
                <mat-hint>Set by the invitation &mdash; cannot be changed</mat-hint>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Cell phone number</mat-label>
                <mat-icon matPrefix>smartphone</mat-icon>
                <input matInput formControlName="cellPhone" placeholder="+1 555 123 4567"
                       mask="(000) 000-0000" [showMaskTyped]="true" autocomplete="tel">
                <mat-hint>Required for SMS two-factor authentication.</mat-hint>
                <mat-error *ngIf="accountForm.get('cellPhone')?.hasError('required')">Cell phone number is required</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Create password</mat-label>
                <mat-icon matPrefix>lock</mat-icon>
                <input matInput formControlName="password"
                       [type]="hidePassword ? 'password' : 'text'"
                       autocomplete="new-password">
                <button type="button" mat-icon-button matSuffix
                        (click)="hidePassword = !hidePassword" tabindex="-1">
                  <mat-icon>{{ hidePassword ? 'visibility_off' : 'visibility' }}</mat-icon>
                </button>
                <mat-hint>Minimum 8 characters</mat-hint>
                <mat-error *ngIf="accountForm.get('password')?.hasError('required')">Password is required</mat-error>
                <mat-error *ngIf="accountForm.get('password')?.hasError('minlength')">Minimum 8 characters</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Confirm password</mat-label>
                <mat-icon matPrefix>lock_outline</mat-icon>
                <input matInput formControlName="confirmPassword"
                       [type]="hideConfirm ? 'password' : 'text'"
                       autocomplete="new-password">
                <button type="button" mat-icon-button matSuffix
                        (click)="hideConfirm = !hideConfirm" tabindex="-1">
                  <mat-icon>{{ hideConfirm ? 'visibility_off' : 'visibility' }}</mat-icon>
                </button>
                <mat-error *ngIf="accountForm.get('confirmPassword')?.hasError('required')">Please confirm your password</mat-error>
                <mat-error *ngIf="accountForm.hasError('passwordMismatch') && accountForm.get('confirmPassword')?.touched">
                  Passwords do not match
                </mat-error>
              </mat-form-field>

            </form>
          </div>

          <div class="step-actions step-actions--right">
            <button mat-flat-button color="primary" matStepperNext
                    (click)="accountForm.markAllAsTouched()">
              Next <mat-icon>arrow_forward</mat-icon>
            </button>
          </div>
        </mat-step>

        <!-- ── Step 2: Facility ─────────────────────────────────── -->
        <mat-step [stepControl]="facilityForm" label="Facility">
          <ng-template matStepLabel>Facility</ng-template>

          <div class="step-content">
            <h3 class="step-title">Set up your facility</h3>
            <p class="step-subtitle">These settings control user access and study expiration times.</p>

            <form [formGroup]="facilityForm" novalidate autocomplete="off">

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Facility name</mat-label>
                <mat-icon matPrefix>local_hospital</mat-icon>
                <input matInput formControlName="facilityName" placeholder="City Medical Center" autocomplete="organization">
                <mat-error *ngIf="facilityForm.get('facilityName')?.hasError('required')">Facility name is required</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Allowed email domain</mat-label>
                <mat-icon matPrefix>alternate_email</mat-icon>
                <input matInput formControlName="emailDomain" placeholder="facility.com">
                <mat-hint>Only users with this domain can be added to your facility. Do not include &#64;.</mat-hint>
                <mat-error *ngIf="facilityForm.get('emailDomain')?.hasError('required')">Email domain is required</mat-error>
                <mat-error *ngIf="facilityForm.get('emailDomain')?.hasError('noAt')">Do not include &#64;</mat-error>
                <mat-error *ngIf="facilityForm.get('emailDomain')?.hasError('invalidDomain')">Enter a valid domain (e.g. facility.com)</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Facility time zone</mat-label>
                <mat-icon matPrefix>schedule</mat-icon>
                <input matInput formControlName="timeZone"
                       placeholder="America/New_York"
                       [matAutocomplete]="tzAuto">
                <mat-autocomplete #tzAuto="matAutocomplete">
                  <mat-option *ngFor="let tz of filteredTimezones" [value]="tz">{{ tz }}</mat-option>
                </mat-autocomplete>
                <mat-hint>Study expiration times are calculated in this time zone.</mat-hint>
                <mat-error *ngIf="facilityForm.get('timeZone')?.hasError('required')">Time zone is required</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Facility contact email</mat-label>
                <mat-icon matPrefix>mail_outline</mat-icon>
                <input matInput formControlName="contactEmail" placeholder="admin@facility.com" type="email">
                <mat-error *ngIf="facilityForm.get('contactEmail')?.hasError('required')">Contact email is required</mat-error>
                <mat-error *ngIf="facilityForm.get('contactEmail')?.hasError('email')">Enter a valid email address</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Facility phone number</mat-label>
                <mat-icon matPrefix>phone</mat-icon>
                <input matInput formControlName="facilityPhone" placeholder="(555) 000-0000"
                       mask="(000) 000-0000" [showMaskTyped]="true" autocomplete="tel">
                <mat-error *ngIf="facilityForm.get('facilityPhone')?.hasError('required')">Facility phone is required</mat-error>
              </mat-form-field>

              <mat-divider class="field-divider"></mat-divider>
              <p class="field-group-label">Facility address</p>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Street address</mat-label>
                <mat-icon matPrefix>location_on</mat-icon>
                <input matInput formControlName="facilityAddress" placeholder="123 Medical Drive" autocomplete="street-address">
                <mat-error *ngIf="facilityForm.get('facilityAddress')?.hasError('required')">Address is required</mat-error>
              </mat-form-field>

              <div class="row-fields">
                <mat-form-field appearance="outline" class="field-city">
                  <mat-label>City</mat-label>
                  <input matInput formControlName="facilityCity" placeholder="Springfield" autocomplete="address-level2">
                  <mat-error *ngIf="facilityForm.get('facilityCity')?.hasError('required')">City is required</mat-error>
                </mat-form-field>

                <mat-form-field appearance="outline" class="field-state">
                  <mat-label>State</mat-label>
                  <input matInput formControlName="facilityState" placeholder="CA"
                         maxlength="2" style="text-transform:uppercase"
                         autocomplete="address-level1">
                  <mat-error *ngIf="facilityForm.get('facilityState')?.hasError('required')">Required</mat-error>
                </mat-form-field>

                <mat-form-field appearance="outline" class="field-zip">
                  <mat-label>ZIP Code</mat-label>
                  <input matInput formControlName="facilityZip" placeholder="90210"
                         maxlength="5" mask="00000" autocomplete="postal-code">
                  <mat-error *ngIf="facilityForm.get('facilityZip')?.hasError('required')">Required</mat-error>
                  <mat-error *ngIf="facilityForm.get('facilityZip')?.hasError('pattern')">5 digits required</mat-error>
                </mat-form-field>
              </div>

              <mat-divider class="field-divider"></mat-divider>
              <p class="field-group-label">Optional</p>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Specialty</mat-label>
                <mat-icon matPrefix>medical_information</mat-icon>
                <input matInput formControlName="specialty"
                       placeholder="Radiology, Skilled Nursing, Rehabilitation…">
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>PACS callback / back URL</mat-label>
                <mat-icon matPrefix>link</mat-icon>
                <input matInput formControlName="pacsUrl" placeholder="https://…" type="url">
                <mat-hint>Only needed if your facility requires a PACS return link.</mat-hint>
              </mat-form-field>

            </form>
          </div>

          <div class="step-actions step-actions--between">
            <button mat-button matStepperPrevious>
              <mat-icon>arrow_back</mat-icon> Back
            </button>
            <button mat-flat-button color="primary" matStepperNext
                    (click)="facilityForm.markAllAsTouched()">
              Next <mat-icon>arrow_forward</mat-icon>
            </button>
          </div>
        </mat-step>

        <!-- ── Step 3: Security ─────────────────────────────────── -->
        <mat-step [stepControl]="securityForm" label="Security">
          <ng-template matStepLabel>Security</ng-template>

          <div class="step-content">
            <h3 class="step-title">Confirm security and usage rules</h3>
            <p class="step-subtitle">NuageDX protects medical data using controlled access, audit logging, and secure exchange workflows.</p>

            <div class="security-checklist">
              <p class="checklist-intro">Before continuing, please confirm that you understand:</p>
              <ul class="checklist">
                <li>
                  <mat-icon class="check-icon">check_circle_outline</mat-icon>
                  Medical images and related documents may contain protected health information.
                </li>
                <li>
                  <mat-icon class="check-icon">check_circle_outline</mat-icon>
                  Access to shared data is controlled by user role and facility membership.
                </li>
                <li>
                  <mat-icon class="check-icon">check_circle_outline</mat-icon>
                  User activity and sharing actions are audited.
                </li>
                <li>
                  <mat-icon class="check-icon">check_circle_outline</mat-icon>
                  Shared studies may expire based on the configured facility time zone.
                </li>
                <li>
                  <mat-icon class="check-icon">check_circle_outline</mat-icon>
                  Staff users should only be invited if they are authorized to access facility data.
                </li>
              </ul>
            </div>

            <form [formGroup]="securityForm" novalidate>
              <mat-checkbox formControlName="accepted" color="primary" class="accept-checkbox">
                I understand and accept these security and usage rules.
              </mat-checkbox>
              <mat-error *ngIf="securityForm.get('accepted')?.hasError('required') && securityForm.get('accepted')?.touched"
                         class="checkbox-error">
                You must accept the security rules to continue
              </mat-error>
            </form>

            <!-- Error banner -->
            <div *ngIf="errorMessage" class="error-banner">
              <mat-icon>error_outline</mat-icon>
              <span>{{ errorMessage }}</span>
            </div>

          </div>

          <div class="step-actions step-actions--between">
            <button mat-button matStepperPrevious [disabled]="state === 'submitting'">
              <mat-icon>arrow_back</mat-icon> Back
            </button>
            <button mat-flat-button color="primary"
                    (click)="onComplete()"
                    [disabled]="securityForm.get('accepted')?.value !== true || state === 'submitting'">
              <mat-spinner *ngIf="state === 'submitting'" diameter="18" class="btn-spinner"></mat-spinner>
              <mat-icon *ngIf="state !== 'submitting'">check</mat-icon>
              {{ state === 'submitting' ? 'Setting up…' : 'Complete setup' }}
            </button>
          </div>
        </mat-step>

      </mat-stepper>
    </mat-card>

  </div>
</div>
